import { createBrowserRouter, Navigate } from "react-router-dom";
import ProtectedLayout from "../containers/ProtectedLayout";
import FirstTimeReset from "../pages/FirstTimeReset";
import EditCompany from "../pages/admin/EditCompany";
import AddUser from "../pages/company/AddUser";
import BulkUserUpload from "../pages/company/BulkUserUpload";
import UpdateListing from "../pages/company/UpdateListing";
import UpdateUser from "../pages/company/UpdateUser";

import Layout from "../containers/Layout";
import Page404 from "../pages/404";
import ForgotPassword from "../pages/ForgotPassword";
import Login from "../pages/Login";
import ResetPassword from "../pages/ResetPassword";
import AddCompany from "../pages/admin/AddCompany";
import AdminDashboard from "../pages/admin/AdminDashboard";
import Companies from "../pages/admin/Companies";
import Notifications from "../pages/admin/notifications";
import Services from "../pages/admin/Services";
import Service from "../pages/admin/Service";
import Account from "../pages/company/Account";
import Amenities from "../pages/company/Amenities";
import CompanyDashboard from "../pages/company/CompanyDashboard";
import Listing from "../pages/company/Listing";
import Listings from "../pages/company/Listings";
import UpcomingListings from "../pages/company/UpcomingListings";
import UpcomingListingDetail from "../pages/company/UpcomingListingDetail";
import Offices from "../pages/company/Offices";
import Users from "../pages/company/UsersNew";
import CompanyServices from "../pages/company/Services";
// import CompanyDirectory from "../pages/company/CompanyDirectory";
import AdminNotifications from "../pages/company/AdminNotifications";
import AppSetting from "../pages/admin/AppSetting";
import CompanyMerge from "../pages/admin/CompanyMerge";

// Integrations pages
import Integrations from "../pages/company/Integrations";
import FUBIntegration from "../pages/company/FUBIntegration";
import AgentIntegrations from "../pages/agent/AgentIntegrations";
import AgentFUBIntegration from "../pages/agent/AgentFUBIntegration";
import NotificationsHistory, {
  useNotificationsHistory,
} from "../pages/admin/NotificationsHistory";

// Agent pages
import AgentDashboard from "../pages/agent/AgentDashboard";
import AgentListings from "../pages/agent/AgentListings";
import AgentListingView from "../pages/agent/AgentListingView";
import AgentBuyers from "../pages/agent/AgentBuyers";
import AgentFavorites from "../pages/agent/AgentFavorites";
import AgentUserDetails from "../pages/agent/AgentUserDetails";
import AgentResetPassword from "../pages/agent/AgentResetPassword";
import AgentServices from "../pages/agent/AgentServices";
import AgentSubCompanies from "../pages/agent/AgentSubCompanies";
import AgentContacts from "../pages/agent/AgentContacts";
import AgentWhiteboard from "../pages/agent/AgentWhiteboard";

// Shared by the agent and company sidebars — the page reads companyId from the
// route and the caller from the token, so one component serves both roles.
import Leaderboard from "../pages/Leaderboard";
import Breadcrumb from "../components/Breadcrumb";

const basePath = process.env.REACT_APP_BASE_URL;

// The standalone agent route has no page wrapper to own the history hook,
// so it supplies the required `history` prop itself.
const AgentNotifications = () => {
  const history = useNotificationsHistory();
  return (
    <>
      <Breadcrumb pageName="Notifications" />
      <NotificationsHistory history={history} />
    </>
  );
};

// Routes for AGENT role
const agentRoutes = [
  {
    path: "agent/dashboard",
    element: <AgentDashboard />,
  },
  {
    path: "agent/whiteboard",
    element: <AgentWhiteboard />,
  },
  {
    path: "agent/leaderboard",
    element: <Leaderboard />,
  },
  {
    path: "agent/listings",
    element: <AgentListings />,
  },
  {
    path: "agent/add-listing",
    element: <Listing />,
  },
  {
    path: "agent/listings/:listingId",
    element: <UpdateListing />,
  },
  {
    path: "agent/listings/:listingId/view",
    element: <AgentListingView />,
  },
  {
    path: "agent/buyers",
    element: <AgentBuyers />,
  },
  {
    path: "agent/integrations",
    element: <AgentIntegrations />,
  },
  {
    path: "agent/integrations/fub/buyer-pool",
    element: <AgentFUBIntegration />,
  },
  // Redirect old agent buyer-pool URL
  {
    path: "agent/buyer-pool",
    element: <Navigate to="../agent/integrations/fub/buyer-pool" replace />,
  },
  {
    path: "agent/favorites",
    element: <AgentFavorites />,
  },
  {
    path: "agent/notifications",
    element: <AgentNotifications />,
  },
  {
    path: "agent/notifications-history",
    element: <Navigate to="../agent/notifications" replace />,
  },
  {
    path: "agent/user-details",
    element: <AgentUserDetails />,
  },
  {
    path: "agent/reset-password",
    element: <AgentResetPassword />,
  },
  {
    path: "agent/services",
    element: <AgentServices />,
  },
  {
    path: "agent/services/:serviceId/sub-companies",
    element: <AgentSubCompanies />,
  },
  {
    path: "agent/services/:serviceId/sub-companies/:subCompanyId/contacts",
    element: <AgentContacts />,
  },
];

const companyRoutes = [
  {
    path: "dashboard",
    element: <CompanyDashboard />,
  },
  {
    path: "listings",
    element: <Listings />,
  },
  {
    path: "upcoming-listings",
    element: <UpcomingListings />,
  },
  {
    path: "upcoming-listings/:listingId",
    element: <UpcomingListingDetail />,
  },
  {
    path: "leaderboard",
    element: <Leaderboard />,
  },
  {
    path: "add-listing",
    element: <Listing />,
  },
  {
    path: "listings/:listingId",
    element: <UpdateListing />,
  },
  {
    path: "offices",
    element: <Offices />,
  },
  {
    path: "users",
    element: <Users />,
  },
  {
    path: "users-upload",
    element: <BulkUserUpload />,
  },
  {
    path: "account",
    element: <Account />,
  },
  {
    path: "add-user",
    element: <AddUser />,
  },
  {
    path: "edit-user/:userId",
    element: <UpdateUser />,
  },
  {
    path: "amenities",
    element: <Amenities />,
  },
  {
    path: "notifications",
    element: <AdminNotifications />,
  },
  {
    path: "services",
    element: <CompanyServices />,
  },
  {
    path: "services/:serviceId",
    // element: <CompanyDirectory />,
    element: <Service />,
  },
  {
    path: "notifications-history",
    element: <Navigate to="../notifications" replace />,
  },
  // Integrations routes
  {
    path: "integrations",
    element: <Integrations />,
  },
  {
    path: "integrations/fub/settings",
    element: <FUBIntegration />,
  },
  {
    path: "integrations/fub/buyer-pool",
    element: <FUBIntegration />,
  },
  // Redirects for old bookmarked URLs
  {
    path: "fub-settings",
    element: <Navigate to="../integrations/fub/settings" replace />,
  },
  {
    path: "buyer-pool",
    element: <Navigate to="../integrations/fub/buyer-pool" replace />,
  },
];

const router = createBrowserRouter(
  [
    {
      element: <Layout />,
      errorElement: <Page404 />,
      children: [
        {
          path: "/",
          element: <Login />,
        },
        {
          path: "/forgot-password",
          element: <ForgotPassword />,
        },
        {
          path: "/reset-password",
          element: <ResetPassword />,
        },
        {
          path: "/first-time",
          element: <FirstTimeReset />,
        },
      ],
    },
    {
      path: "/administrator",
      element: <ProtectedLayout />,
      children: [
        {
          path: "dashboard",
          element: <AdminDashboard />,
        },
        {
          path: "companies",
          element: <Companies />,
        },
        {
          path: "company-merge",
          element: <CompanyMerge />,
        },
        {
          path: "add-company",
          element: <AddCompany />,
        },
        {
          path: "edit-company/:companyId",
          element: <EditCompany />,
        },
        {
          path: "notifications",
          element: <Notifications />,
        },
        {
          path: "services",
          element: <Services />,
        },
        {
          path: "services/:serviceId",
          element: <Service />,
        },
        {
          path: "app-settings",
          element: <AppSetting />,
        },
        {
          path: "notifications-history",
          element: <Navigate to="../notifications" replace />,
        },
      ],
    },
    {
      path: "/company/:companyId",
      element: <ProtectedLayout />,
      children: [...companyRoutes, ...agentRoutes],
    },
  ],
  { basename: basePath }
);

export default router;
